import React, {
  type CSSProperties,
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import {
  type Cell,
  type CellProps,
  type Column,
  type ColumnInstance,
  type TableRowProps as ReactTableRowProps,
  useExpanded,
  useSortBy,
  useTable,
  type Row as TRow,
} from 'react-table';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import EnhancedMarkdownCenter from '../EnhancedMarkdown/components/EnhancedMarkdownCenter';
import EnhancedMarkdownZmdiIcon from '../EnhancedMarkdown/components/EnhancedMarkdownZmdiIcon';
import EnhancedMarkdownStyled, {
  buildEnhancedMarkdownStyledShorthands,
} from '../EnhancedMarkdown/components/EnhancedMarkdownStyled';
import EnhancedMarkdownActorIcon from '../EnhancedMarkdown/components/EnhancedMarkdownActorIcon';
import EnhancedMarkdownArrow, {
  buildEnhancedMarkdownArrowShortHands,
} from '../EnhancedMarkdown/components/EnhancedMarkdownArrow';
import EnhancedMarkdownEncounterIcon from '../EnhancedMarkdown/components/EnhancedMarkdownEncounterIcon';
import EnhancedMarkdownItemIcon from '../EnhancedMarkdown/components/EnhancedMarkdownItemIcon';
import EnhancedMarkdownItemSetIcon from '../EnhancedMarkdown/components/EnhancedMarkdownItemSetIcon';
import EnhancedMarkdownBar from '../EnhancedMarkdown/components/EnhancedMarkdownBar';
import useTheme from '../../contexts/useTheme';
import Button, { type ButtonStyle } from '../Button/Button';
import EnhancedMarkdownIcon from '../EnhancedMarkdown/components/EnhancedMarkdownIcon';
import EnhancedMarkdownAbilityIcon from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownAbilityIcon';
import EnhancedMarkdownGearIcon from '../EnhancedMarkdown/components/EnhancedMarkdownGearIcon';
import useTableHiddenColumns from '@resources/js/contexts/useTableHiddenColumns';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import EnhancedMarkdownTooltip from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownTooltip';
import EnhancedMarkdownBadgeLabel from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownBadgeLabel';
import EnhancedMarkdownSiteLogo from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownSiteLogo';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import { renderToStaticMarkup } from 'react-dom/server';
import EnhancedMarkdownHealthBar from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownHealthBar';
import cx from 'classnames';
import EnhancedMarkdownZoneIcon from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownZoneIcon';

export type TableProps<
  Row extends Record<string, unknown> = Record<string, unknown>,
> = {
  style?: TableStyle;
  columns: Record<string, TableColumn<Row> | null | undefined>;
  data: readonly Row[];
  footer?: Row;
  enhancedMarkdownEnabled?: boolean;
  truncate?: {
    limit?: number;
    showMoreButton: {
      styles: ButtonStyle[] | 'inline-row';
    };
  };
  sorting?: {
    initialColumn: string | null;
    showControls?: boolean;
  };
  draggable?: {
    onRowOrderChange?: (rows: Row[]) => void;
  };
  onRowClick?: (row: TRow<Row>) => void;
  isRowClickDisabled?: (row: TRow<Row>) => boolean;
  wrapperStyle?: React.CSSProperties;
  getRowClassName?: (row: TRow<Row>) => string | undefined;
  isRowExpandDisabled?: (row: TRow<Row>) => boolean;
  renderExpandedContent?: (row: TRow<Row>) => JSX.Element | null;
  initialExpandedRowIndices?: number[];
  /** Expands rows once they have sub rows, so needs a stable `getRowId`. */
  defaultExpanded?: boolean;
  getRowId?: (row: Row, relativeIndex: number, parent?: TRow<Row>) => string;
};

export type TableStyle =
  | 'dark-gray-striped'
  | 'dark-blue-striped'
  | 'report'
  | 'v2'
  | 'v2-bordered'
  | 'v2-slim';

type SortingDirection = 'asc' | 'desc';

export type TableColumn<
  Row extends Record<string, unknown> = Record<string, unknown>,
> = {
  header: string | JSX.Element;
  title?: string;
  width?: number | string;
  maxWidth?: number;
  minWidth?: number;
  noWrap?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  columns?: Record<string, TableColumn<Row>>;
  renderCell?: (cell: CellProps<Row>) => JSX.Element;
  renderFooter?: (column: ColumnInstance<Row>, footer?: Row) => JSX.Element;
  displayValueKey?: string;
  sorting?: {
    initialDirection: SortingDirection;
  };
  expandable?: {
    expanderPosition: 'left' | 'right';
    /** Defaults to true */
    indentSubRows?: boolean;
  };
  /** Spans the cell over the next visible columns. It drops the column's widths, which would otherwise cap it. */
  getColSpan?: (row: TRow<Row>) => number;
};

const defaultWrapperStyle = {};

function Table<Row extends Record<string, unknown>>({
  style,
  columns: nullableColumns,
  data,
  footer,
  enhancedMarkdownEnabled = false,
  truncate,
  sorting,
  draggable,
  onRowClick,
  isRowClickDisabled,
  wrapperStyle = defaultWrapperStyle,
  getRowClassName,
  isRowExpandDisabled,
  renderExpandedContent,
  initialExpandedRowIndices,
  defaultExpanded = false,
  getRowId,
}: TableProps<Row>): JSX.Element {
  const { hiddenColumns } = useTableHiddenColumns();

  const [orderedData, setOrderedData] = useState<readonly Row[]>(data);
  useEffect(() => {
    setOrderedData(data);
  }, [data]);

  const columns = useMemo(
    () =>
      Object.entries(nullableColumns).reduce<
        Record<string, NonNullable<TableColumn<Row>>>
      >((acc, [key, value]) => {
        if (!value) {
          return acc;
        }

        const column: TableColumn<Row> = {
          ...value,
          sorting: sorting && value?.sorting,
        };

        acc[key] = column;
        return acc;
      }, {}),
    [nullableColumns, sorting],
  );

  const flatColumns = useMemo(() => toFlatColumns(columns), [columns]);

  const reactTableColumns = useMemo(
    () => toReactTableColumns(columns, footer),
    [columns, footer],
  );

  const [showMore, setShowMore] = useState(false);

  const initialState = useMemo(
    () => ({
      hiddenColumns,
      ...(sorting
        ? {
            sortBy: sorting.initialColumn
              ? [
                  {
                    id: sorting.initialColumn,
                    desc:
                      flatColumns[sorting.initialColumn].sorting
                        ?.initialDirection === 'desc',
                  },
                ]
              : [],
          }
        : {}),
      ...(initialExpandedRowIndices
        ? {
            expanded: Object.fromEntries(
              initialExpandedRowIndices.map((index) => [index, true]),
            ),
          }
        : {}),
    }),
    [flatColumns, hiddenColumns, sorting, initialExpandedRowIndices],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    rowsById,
    prepareRow,
    setHiddenColumns,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    toggleRowExpanded,
  } = useTable<Row>(
    {
      columns: reactTableColumns,
      data: draggable ? orderedData : data,
      // TODO: Migrate to @tanstack/react-table for proper typing
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialState,
      // TODO: Migrate to @tanstack/react-table for proper typing
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      disableSortRemove: true,
      autoResetSortBy: false,
      autoResetExpanded: !defaultExpanded,
      getRowId,
    },
    useSortBy,
    useExpanded,
  );

  const seenExpandableRowIds = useRef(new Set<string>());
  useLayoutEffect(
    function expandNewExpandableRows() {
      if (!defaultExpanded) {
        return;
      }

      Object.values(rowsById).forEach((row) => {
        if (row.subRows.length && !seenExpandableRowIds.current.has(row.id)) {
          seenExpandableRowIds.current.add(row.id);
          (toggleRowExpanded as (id: string, value: boolean) => void)(
            row.id,
            true,
          );
        }
      });
    },
    [defaultExpanded, rowsById, toggleRowExpanded],
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [hiddenColumns, setHiddenColumns]);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      const newData = arrayMove([...orderedData], oldIndex, newIndex);
      setOrderedData(newData);
      draggable?.onRowOrderChange?.(newData);
    },
    [draggable, orderedData, rows],
  );
  const dragItems = useMemo(() => rows.map((r) => r.id), [rows]);

  const hiddenRows = useMemo(() => {
    const hiddenRows = new Set();
    if (showMore || !truncate?.limit) {
      return hiddenRows;
    }
    let shownTopLevelRows = 0;
    let lastTopLevelRowWasShown = false;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
      const depth = row.depth ?? 0;
      if (depth > 0 && lastTopLevelRowWasShown) {
        continue;
      }
      if (shownTopLevelRows >= truncate.limit) {
        hiddenRows.add(row);
        lastTopLevelRowWasShown = false;
      } else {
        shownTopLevelRows++;
        lastTopLevelRowWasShown = true;
      }
    }
    return hiddenRows;
  }, [rows, truncate?.limit, showMore]);

  const theme = useTheme();
  style = style ?? theme.tableStyle;
  let className = undefined;
  switch (style) {
    case 'dark-gray-striped':
      className = 'dark-gray-striped-table';
      break;
    case 'dark-blue-striped':
      className = 'dark-striped-table';
      break;
    case 'report':
      className = 'react-table react-table--report';
      break;
    case 'v2':
      className = 'react-table react-table--v2';
      break;
    case 'v2-bordered':
      className = 'react-table react-table--v2 react-table--v2--bordered';
      break;
    case 'v2-slim':
      className = 'react-table react-table--v2 react-table--v2--slim';
      break;
  }

  const enhancedMarkdownOverrides = useMemo(
    () => getTableEnhancedMarkdownOverrides(style),
    [style],
  );

  const enhancedMarkdownOptions = useMemo(
    () => ({
      lazyLoad: false,
    }),
    [],
  );

  const showSortingControls = sorting?.showControls ?? true;

  const tableContent = (
    <EnhancedMarkdownOptionsContextProvider value={enhancedMarkdownOptions}>
      <div className={'react-table__wrapper'} style={wrapperStyle}>
        <div
          className={'react-table__overflow-wrapper'}
          style={draggable ? { overflowY: 'hidden' } : undefined} // Avoids scrollbars flickering when dragging rows
        >
          <table className={className} {...getTableProps({ role: undefined })}>
            {Object.values(columns).some((column) => column.header) && (
              <thead>
                {headerGroups.map((headerGroup) => {
                  const headerGroupProps = headerGroup.getHeaderGroupProps({
                    role: undefined,
                  });
                  return (
                    <tr {...headerGroupProps} key={headerGroupProps.key}>
                      {draggable && <th />}
                      {headerGroup.headers.map((column) => {
                        const options = flatColumns[column.id];
                        const headerProps = column.getHeaderProps({
                          ...(options?.sorting
                            ? // TODO: Migrate to @tanstack/react-table for proper typing
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              column.getSortByToggleProps()
                            : undefined),
                          role: undefined,
                          className: `react-table__cell--${column.id} ${
                            options?.noWrap ? 'react-table__cell--no-wrap' : ''
                          }`,
                        });

                        return (
                          <th
                            {...headerProps}
                            key={headerProps.key}
                            style={{
                              ...column.getHeaderProps().style,
                              ...getColumnStyle(true, options),
                              ...{ textAlign: options?.textAlign ?? 'center' },
                            }}
                            title={options?.title}
                          >
                            <Wrapper
                              enhancedMarkdownEnabled={enhancedMarkdownEnabled}
                              enhancedMarkdownOverrides={
                                enhancedMarkdownOverrides
                              }
                            >
                              {column.render('Header')}
                              {options?.sorting && showSortingControls ? (
                                <div className={'react-table__sorting-control'}>
                                  {
                                    // TODO: Migrate to @tanstack/react-table for proper typing
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    column.isSorted ? (
                                      // TODO: Migrate to @tanstack/react-table for proper typing
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      column.isSortedDesc ? (
                                        <AscendingSortIcon style={style} />
                                      ) : (
                                        <DescendingSortIcon style={style} />
                                      )
                                    ) : (
                                      <UnsortedIcon style={style} />
                                    )
                                  }
                                </div>
                              ) : null}
                            </Wrapper>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
            )}
            <tbody {...getTableBodyProps({ role: undefined })}>
              {rows
                .filter((row) => !hiddenRows.has(row))
                .map((row) => {
                  prepareRow(row);
                  const cells = row.cells;
                  const rowProps = row.getRowProps({ role: undefined });

                  return (
                    <TableRowRenderer
                      key={rowProps.key}
                      row={row}
                      cells={cells}
                      rowProps={rowProps}
                      flatColumns={flatColumns}
                      isDraggable={Boolean(draggable)}
                      onRowClick={onRowClick}
                      isRowClickDisabled={isRowClickDisabled}
                      getRowClassName={getRowClassName}
                      isRowExpandDisabled={isRowExpandDisabled}
                      renderExpandedContent={renderExpandedContent}
                      enhancedMarkdownEnabled={enhancedMarkdownEnabled}
                      enhancedMarkdownOverrides={enhancedMarkdownOverrides}
                    />
                  );
                })}
              {truncate?.limit &&
                data.length > truncate.limit &&
                truncate.showMoreButton.styles === 'inline-row' && (
                  <tr>
                    <td
                      colSpan={1000}
                      onClick={() => setShowMore(!showMore)}
                      className={'react-table__truncate-row'}
                    >
                      {showMore
                        ? trans('global.show-less')
                        : trans('global.show-more')}
                    </td>
                  </tr>
                )}
            </tbody>
            {footer && (
              <tfoot>
                {footerGroups.map((group) => {
                  const footerGroupProps = group.getFooterGroupProps();
                  return (
                    <tr {...footerGroupProps} key={footerGroupProps.key}>
                      {group.headers.map((column) => {
                        const options = flatColumns[column.id];
                        const footerProps = column.getFooterProps([
                          {
                            role: undefined,
                            className: `react-table__cell--${column.id} ${
                              options?.noWrap
                                ? 'react-table__cell--no-wrap'
                                : ''
                            }`,
                          },
                        ]);

                        return (
                          <td
                            {...footerProps}
                            key={footerProps.key}
                            style={{
                              ...footerProps.style,
                              ...getColumnStyle(false, options),
                              ...{ textAlign: options?.textAlign ?? 'left' },
                            }}
                          >
                            <Wrapper
                              enhancedMarkdownEnabled={enhancedMarkdownEnabled}
                              enhancedMarkdownOverrides={
                                enhancedMarkdownOverrides
                              }
                            >
                              {(
                                column.Footer as ({
                                  column,
                                }: {
                                  column: unknown;
                                }) => JSX.Element
                              )({ column })}
                            </Wrapper>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tfoot>
            )}
          </table>
        </div>
        {truncate?.limit &&
          data.length > truncate.limit &&
          truncate.showMoreButton.styles !== 'inline-row' && (
            <Button
              onClick={() => setShowMore(!showMore)}
              styles={truncate.showMoreButton.styles}
            >
              {showMore ? trans('global.show-less') : trans('global.show-more')}
            </Button>
          )}
      </div>
    </EnhancedMarkdownOptionsContextProvider>
  );

  if (!draggable) {
    return tableContent;
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={dragItems} strategy={verticalListSortingStrategy}>
        {tableContent}
      </SortableContext>
    </DndContext>
  );
}

type TableRowProps<
  Row extends Record<string, unknown> = Record<string, unknown>,
> = {
  row: TRow<Row>;
  cells: Cell<Row>[];
  rowProps: ReactTableRowProps;
  flatColumns: Record<string, TableColumn<Row>>;
  isDraggable: boolean;
  onRowClick?: (row: TRow<Row>) => void;
  isRowClickDisabled?: (row: TRow<Row>) => boolean;
  getRowClassName?: (row: TRow<Row>) => string | undefined;
  isRowExpandDisabled?: (row: TRow<Row>) => boolean;
  renderExpandedContent?: (row: TRow<Row>) => JSX.Element | null;
  enhancedMarkdownEnabled: boolean;
  enhancedMarkdownOverrides: Record<string, unknown>;
  sortable?: ReturnType<typeof useSortable>;
};

function TableRowRenderer<Row extends Record<string, unknown>>(
  props: TableRowProps<Row>,
): JSX.Element {
  return props.isDraggable ? (
    <DraggableTableRow {...props} />
  ) : (
    <TableRow {...props} />
  );
}

function DraggableTableRow<Row extends Record<string, unknown>>(
  props: TableRowProps<Row>,
): JSX.Element {
  const sortable = useSortable({
    id: props.row.id,
    animateLayoutChanges: () => false,
  });

  return <TableRow {...props} sortable={sortable} />;
}

function TableRow<Row extends Record<string, unknown>>({
  row,
  cells,
  rowProps,
  flatColumns,
  isDraggable,
  onRowClick,
  isRowClickDisabled,
  getRowClassName,
  isRowExpandDisabled,
  renderExpandedContent,
  enhancedMarkdownEnabled,
  enhancedMarkdownOverrides,
  sortable,
}: TableRowProps<Row>): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortable ?? {};

  // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
  const isExpanded = row.isExpanded;
  const columnCount = Object.keys(flatColumns).length;
  const canClick = !isDraggable && onRowClick && !isRowClickDisabled?.(row);
  if (transform) {
    // dnd-kit stretches elements to fit into the drop zone, but this looks weird when dragging variable height rows
    transform.scaleX = 1;
    transform.scaleY = 1;
  }
  const dragStyle: CSSProperties = {
    transform:
      transform?.x || transform?.y
        ? CSS.Transform.toString(transform)
        : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Fragment key={rowProps.key}>
      <tr
        {...rowProps}
        key={rowProps.key}
        ref={isDraggable ? setNodeRef : undefined}
        style={
          isDraggable ? { ...rowProps.style, ...dragStyle } : rowProps.style
        }
        onClick={canClick ? () => onRowClick!(row) : undefined}
        className={cx(
          canClick && 'react-table__row--selectable',
          getRowClassName?.(row),
        )}
      >
        {isDraggable && (
          <td
            {...attributes}
            {...listeners}
            style={{ cursor: 'grab', width: 24, textAlign: 'center' }}
          >
            ⠿
          </td>
        )}
        <TableRowCells
          key={`${String(rowProps.key)}-cells`}
          row={row}
          cells={cells}
          flatColumns={flatColumns}
          renderExpandedContent={renderExpandedContent}
          isRowExpandDisabled={isRowExpandDisabled}
          enhancedMarkdownEnabled={enhancedMarkdownEnabled}
          enhancedMarkdownOverrides={enhancedMarkdownOverrides}
        />
      </tr>

      {isExpanded && renderExpandedContent && (
        <tr>
          <td
            colSpan={columnCount + (isDraggable ? 1 : 0)}
            style={{ padding: 0 }}
          >
            {renderExpandedContent(row)}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

type TableRowCellsProps<
  Row extends Record<string, unknown> = Record<string, unknown>,
> = {
  row: TRow<Row>;
  cells: Cell<Row>[];
  flatColumns: Record<string, TableColumn<Row>>;
  renderExpandedContent?: (row: TRow<Row>) => JSX.Element | null;
  isRowExpandDisabled?: (row: TRow<Row>) => boolean;
  enhancedMarkdownEnabled: boolean;
  enhancedMarkdownOverrides: Record<string, unknown>;
};

function TableRowCells<Row extends Record<string, unknown>>({
  row,
  cells,
  flatColumns,
  renderExpandedContent,
  isRowExpandDisabled,
  enhancedMarkdownEnabled,
  enhancedMarkdownOverrides,
}: TableRowCellsProps<Row>): JSX.Element {
  const coveredCells = new Set<Cell<Row>>();

  return (
    <>
      {cells.map((cell, index) => {
        if (coveredCells.has(cell)) {
          return null;
        }

        const options = flatColumns[cell.column.id];
        const colSpan = Math.max(1, options?.getColSpan?.(row) ?? 1);
        cells
          .slice(index + 1, index + colSpan)
          .forEach((covered) => coveredCells.add(covered));
        const cellProps = cell.getCellProps([
          {
            role: undefined,
            className: `react-table__cell--${cell.column.id} ${
              options?.noWrap ? 'react-table__cell--no-wrap' : ''
            }`,
          },
        ]);
        const canExpand =
          // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
          cell.row.canExpand || renderExpandedContent;
        const isExpandDisabled = isRowExpandDisabled?.(row);

        return (
          <td
            {...cellProps}
            key={cellProps.key}
            colSpan={colSpan > 1 ? colSpan : undefined}
            style={{
              ...cellProps.style,
              ...(colSpan > 1 ? {} : getColumnStyle(false, options)),
              ...{
                textAlign: options?.textAlign ?? 'left',
                paddingLeft:
                  options.expandable &&
                  options.expandable.indentSubRows !== false &&
                  // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                  row.depth
                    ? // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                      row.depth * 26
                    : undefined,
              },
            }}
          >
            <TableCellContents
              cell={cell}
              options={options}
              canExpand={canExpand}
              isExpandDisabled={isExpandDisabled}
              enhancedMarkdownEnabled={enhancedMarkdownEnabled}
              enhancedMarkdownOverrides={enhancedMarkdownOverrides}
            />
          </td>
        );
      })}
    </>
  );
}

type RowExpanderProps<
  Row extends Record<string, unknown> = Record<string, unknown>,
> = {
  cell: Cell<Row>;
  options: TableColumn<Row>;
  canExpand: boolean;
  isExpandDisabled?: boolean;
  enhancedMarkdownEnabled: boolean;
  enhancedMarkdownOverrides: Record<string, unknown>;
};

function TableCellContents<Row extends Record<string, unknown>>({
  cell,
  options,
  canExpand,
  isExpandDisabled,
  enhancedMarkdownOverrides,
  enhancedMarkdownEnabled,
}: RowExpanderProps<Row>) {
  const expanderPosition = options.expandable?.expanderPosition;
  const shouldShowExpander = expanderPosition && canExpand;

  const row = (
    <Wrapper
      enhancedMarkdownEnabled={enhancedMarkdownEnabled}
      enhancedMarkdownOverrides={enhancedMarkdownOverrides}
    >
      {(cell.column.Cell as (cell: unknown) => JSX.Element)(cell)}
    </Wrapper>
  );

  if (!canExpand || !shouldShowExpander) {
    return row;
  }

  // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
  const isExpanded = cell.row.isExpanded;

  const expander = (
    <Button
      styles={['unstyled']}
      className={cx('react-table__expander-button', {
        ['react-table__expander-button--disabled']: isExpandDisabled,
      })}
      disabled={isExpandDisabled}
      aria-expanded={isExpanded}
      onClick={() =>
        // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
        cell.row.toggleRowExpanded(!isExpanded)
      }
    >
      <ExpanderIcon isExpanded={isExpanded} />
    </Button>
  );

  return (
    <HorizontalContent
      style={{
        justifyContent:
          expanderPosition === 'left' ? undefined : 'space-between',
      }}
    >
      {expanderPosition === 'left' ? (
        <>
          {expander}
          {row}
        </>
      ) : (
        <>
          {row}
          {expander}
        </>
      )}
    </HorizontalContent>
  );
}

function getTableEnhancedMarkdownOverrides<Row extends Record<string, unknown>>(
  style: TableProps<Row>['style'],
) {
  let AbilityIcon = EnhancedMarkdownAbilityIcon;
  let ActorIcon = EnhancedMarkdownActorIcon;
  let ItemIcon = EnhancedMarkdownItemIcon;
  let ItemSetIcon = EnhancedMarkdownItemSetIcon;
  let EncounterIcon = EnhancedMarkdownEncounterIcon;
  let ZoneIcon = EnhancedMarkdownZoneIcon;
  let ImageIcon = EnhancedMarkdownIcon;
  let GearIcon = EnhancedMarkdownGearIcon;
  if (style === 'report') {
    AbilityIcon = (props) => (
      <EnhancedMarkdownAbilityIcon rounded={false} {...props} />
    );

    ActorIcon = (props) => (
      <EnhancedMarkdownActorIcon rounded={false} bordered={true} {...props} />
    );

    ItemIcon = (props) => (
      <EnhancedMarkdownItemIcon rounded={false} bordered={true} {...props} />
    );

    ItemSetIcon = (props) => (
      <EnhancedMarkdownItemSetIcon rounded={false} bordered={true} {...props} />
    );

    EncounterIcon = (props) => (
      <EnhancedMarkdownEncounterIcon
        rounded={false}
        bordered={true}
        {...props}
      />
    );

    ZoneIcon = (props) => (
      <EnhancedMarkdownZoneIcon rounded={false} bordered={true} {...props} />
    );

    ImageIcon = (props) => (
      <EnhancedMarkdownIcon rounded={false} bordered={true} {...props} />
    );

    GearIcon = (props) => (
      <EnhancedMarkdownGearIcon rounded={false} bordered={true} {...props} />
    );
  }

  return {
    Center: EnhancedMarkdownCenter,
    Icon: EnhancedMarkdownZmdiIcon,
    Styled: EnhancedMarkdownStyled,
    Bar: EnhancedMarkdownBar,
    HealthBar: EnhancedMarkdownHealthBar,
    AbilityIcon,
    ActorIcon,
    ...buildEnhancedMarkdownStyledShorthands(),
    ItemIcon,
    ItemSetIcon,
    EncounterIcon,
    ZoneIcon,
    ImageIcon,
    GearIcon,
    Arrow: EnhancedMarkdownArrow,
    Tooltip: EnhancedMarkdownTooltip,
    BadgeLabel: EnhancedMarkdownBadgeLabel,
    SiteLogo: EnhancedMarkdownSiteLogo,
    HorizontalContent,
    ...buildEnhancedMarkdownArrowShortHands(),
  };
}

function getColumnStyle<Row extends Record<string, unknown>>(
  isHeader: boolean,
  options?: TableColumn<Row>,
): React.CSSProperties {
  if (!options) {
    return {};
  }

  return {
    width: options.width,
    minWidth: options.minWidth,
    maxWidth: options.maxWidth,
    ...(isHeader && options.sorting
      ? {
          cursor: 'pointer',
          userSelect: 'none',
        }
      : {}),
  };
}

function toFlatColumns<Row extends Record<string, unknown>>(
  columns: Record<string, TableColumn<Row>>,
): Record<string, TableColumn<Row>> {
  let flatColumns = { ...columns };
  Object.values(columns).forEach((options) => {
    if (options.columns) {
      flatColumns = { ...flatColumns, ...toFlatColumns(options.columns) };
    }
  });
  return flatColumns;
}

const sortType = (
  rowA: { original: Record<string, unknown> },
  rowB: { original: Record<string, unknown> },
  columnId: string,
) => {
  const valueA = rowA.original[columnId];
  const valueB = rowB.original[columnId];

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueA.localeCompare(valueB);
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }

  return 0;
};

function toReactTableColumns<Row extends Record<string, unknown>>(
  columns: Record<string, TableColumn<Row>>,
  footer?: Row,
): Column<Row>[] {
  return Object.entries(columns).map<Column<Row>>(([accessor, options]) => {
    const Cell =
      options.renderCell ??
      ((cell: CellProps<Row>) =>
        options.displayValueKey
          ? (cell.row.original[options.displayValueKey] ?? cell.value)
          : cell.value);

    const Footer = ({ column }: { column: ColumnInstance<Row> }): ReactNode =>
      options.renderFooter?.(column, footer) ??
      (options.displayValueKey
        ? (footer?.[options.displayValueKey] as ReactNode)
        : undefined) ??
      (footer?.[column.id] as ReactNode) ??
      '';

    return {
      id: accessor,
      accessor,
      Header: options.header,
      Cell: Cell,
      Footer: Footer,
      width: options.width,
      maxWidth: options.maxWidth,
      minWidth: options.minWidth,
      sortDescFirst: options.sorting?.initialDirection === 'desc',
      sortType,
      columns: options.columns
        ? toReactTableColumns(options.columns)
        : undefined,
    };
  });
}

const EnhancedMarkdownWrapper = memo(function EnhancedMarkdownWrapper({
  children,
  enhancedMarkdownOverrides,
}: {
  children: ReactNode;
  enhancedMarkdownOverrides: Record<string, unknown>;
}) {
  if (Array.isArray(children)) {
    // Deal with each child separately
    return (
      <>
        {children.map((child, index) => (
          <EnhancedMarkdownWrapper
            key={index}
            enhancedMarkdownOverrides={enhancedMarkdownOverrides}
          >
            {child}
          </EnhancedMarkdownWrapper>
        ))}
      </>
    );
  }

  let markdown: string | undefined;

  // First check if the child component is a string
  if (typeof children === 'string') {
    markdown = children;
  }

  if (!markdown && markdown !== '') {
    // Else check if the child component has a value prop (i.e. it is a table cell)
    markdown = (
      children as {
        props: { value: string };
      }
    )?.props?.value?.toString();
  }

  if (!markdown && markdown !== '') {
    // Else check if the child type is "Footer" (i.e. it is a footer cell)
    if (
      children &&
      typeof children === 'object' &&
      'type' in children &&
      typeof children.type === 'string' &&
      children.type === 'Footer'
    ) {
      // "Footer" is a react-table internal component, so we render it to get the markdown
      markdown = renderToStaticMarkup(children);
    }
  }

  // If we have no markdown at this point, the child is likely a React component, so just return it
  if (!markdown && markdown !== '') {
    return <>{children}</>;
  }

  return (
    <EnhancedMarkdown
      markdown={markdown}
      wrapped={false}
      overrides={enhancedMarkdownOverrides}
      onlyUsePassedOverrides={true}
    />
  );
});

const Wrapper = memo(function Wrapper({
  children,
  enhancedMarkdownEnabled,
  enhancedMarkdownOverrides,
}: {
  children: ReactNode;
  enhancedMarkdownEnabled: boolean;
  enhancedMarkdownOverrides: Record<string, unknown>;
}) {
  if (enhancedMarkdownEnabled) {
    return (
      <EnhancedMarkdownWrapper
        enhancedMarkdownOverrides={enhancedMarkdownOverrides}
      >
        {children}
      </EnhancedMarkdownWrapper>
    );
  }

  return <>{children}</>;
});

export const UnsortedIcon = memo(function UnsortedIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='16'
      viewBox='0 0 17 24'
      fill='#808080'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? -5 : -1 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 0L0 9H17L8.5 0Z' />
        <path d='M8.5 24L17 15L7.86805e-07 15L8.5 24Z' />
      </g>
    </svg>
  );
});

export const AscendingSortIcon = memo(function AscendingSortIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='17'
      height='9'
      viewBox='0 0 17 9'
      fill='#DEDEDE'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? 0 : 2 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 9L17 1.90735e-06L7.86805e-07 4.21161e-07L8.5 9Z' />
      </g>
    </svg>
  );
});

export const DescendingSortIcon = memo(function DescendingSortIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='17'
      height='9'
      viewBox='0 0 17 9'
      fill='#DEDEDE'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? 0 : 2 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 0L0 9H17L8.5 0Z' />
      </g>
    </svg>
  );
});

export const ExpanderIcon = memo(function ExpanderIcon({
  isExpanded = false,
}: {
  isExpanded?: boolean;
}) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='17'
      height='17'
      viewBox='0 0 17 17'
      fill='#DEDEDE'
      className={'react-table__expander-icon'}
      style={{
        transform: `rotate(${isExpanded ? 90 : 0}deg)`,
      }}
    >
      <path d='M11.875 8.5L5.125 14.875V2.125L11.875 8.5Z' />
    </svg>
  );
});

export default Table;
